/**
 * EditShareGrantDialog — shared dialog for editing an EXISTING share grant's
 * TTL and password (Task 11). Two entry points mount this same component
 * with the same mutation:
 *   - SharedLinksTab's per-row "Edit" action (Settings → Links).
 *   - The gear icon in SharePopover's active-grant state (Task 15, not yet
 *     built) — deliberately not imported/coupled here so that wiring is a
 *     drop-in later, not a refactor.
 *
 * Deliberately self-contained: reads campaignId straight off
 * `useCampaignUiStore` (same pattern `SharePopover` and `SharedLinksTab`
 * already use) rather than taking it as a prop — every mount site lives
 * inside the same campaign-detail provider tree, so there is nothing
 * caller-specific to thread through.
 * The only required prop is the grant being edited.
 *
 * TTL segmented control mirrors SharePopover's markup/labels exactly, using
 * the SAME `TTL_OPTIONS`/`TTL_HUMAN`/`closestTtlPreset` constants (extracted
 * to `shareGrantTtl.ts` by this task specifically so the two surfaces can't
 * drift). Password section branches on the grant's CURRENT
 * `password_protected` state:
 *   - unprotected → single "Add password" affordance, pre-filled with a
 *     fresh `generateRandomSharePassword()` value (same helper SharePopover's
 *     mint flow uses) with a "Generate" button.
 *   - protected → "Regenerate password" and "Remove password" as two
 *     buttons whose actions are mutually exclusive by construction: both
 *     write into the SAME `passwordAction` state variable, so only one can
 *     ever be selected, and `buildUpdateInput` below only ever attaches ONE
 *     of `password` / `remove_password` to the PATCH payload — never both,
 *     regardless of click order.
 */
import { useState } from 'react';
import {
  useCampaignUiStore,
  useUpdateShareGrant,
  type ShareGrantDto,
  type ShareGrantTtl,
  type UpdateShareGrantInput,
} from '@discovr/features';
import { Lock, RefreshCw } from '../../icons';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { generateRandomSharePassword } from './defaultSharePassword';
import { SHARE_DIALOG_CLASS } from './shareDialogChrome';
import { TTL_OPTIONS, TTL_HUMAN, closestTtlPreset } from './shareGrantTtl';

export interface EditShareGrantDialogProps {
  /** The grant being edited. Its `password_protected` value decides which
   *  password affordance renders — this dialog never guesses that state,
   *  it always reads it off the grant the caller passed in. */
  grant: ShareGrantDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** No password change selected yet — an "unchanged" TTL-only edit sends
 *  neither `password` nor `remove_password`. `add`/`regenerate` are
 *  cosmetically distinct (different copy, different affordance for an
 *  unprotected vs. protected grant) but behave identically on submit — both
 *  send the current `passwordValue` as `password`. */
type PasswordAction = 'unchanged' | 'add' | 'regenerate' | 'remove';

/**
 * Builds the PATCH payload from local dialog state. The mutual-exclusivity
 * guarantee lives here: `password` and `remove_password` are attached by an
 * if/else-if chain over the SAME `passwordAction` value, so the returned
 * object can never carry both keys — there is no code path that sets both,
 * unlike relying on the BE's 400 to catch a bad combination client-side.
 */
function buildUpdateInput(
  ttl: ShareGrantTtl,
  passwordAction: PasswordAction,
  passwordValue: string,
): UpdateShareGrantInput {
  if (passwordAction === 'add' || passwordAction === 'regenerate') {
    return { ttl, password: passwordValue };
  }
  if (passwordAction === 'remove') {
    return { ttl, remove_password: true };
  }
  return { ttl };
}

export function EditShareGrantDialog({ grant, open, onOpenChange }: EditShareGrantDialogProps) {
  const campaignId = useCampaignUiStore((s) => s.campaignId) ?? '';
  const updateGrant = useUpdateShareGrant(campaignId);

  const [ttl, setTtl] = useState<ShareGrantTtl>(() => closestTtlPreset(grant));
  const [passwordAction, setPasswordAction] = useState<PasswordAction>('unchanged');
  const [passwordValue, setPasswordValue] = useState('');

  // Reset local state whenever the dialog transitions closed -> open, so a
  // previous grant's half-finished edit never bleeds into the next one —
  // same adjust-during-render pattern SharePopover/PublicLoginDialog use.
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setTtl(closestTtlPreset(grant));
      setPasswordAction('unchanged');
      setPasswordValue(generateRandomSharePassword());
    }
  }

  const handleAddPassword = () => {
    setPasswordAction('add');
    setPasswordValue(generateRandomSharePassword());
  };

  const handleRegeneratePassword = () => {
    setPasswordAction('regenerate');
    setPasswordValue(generateRandomSharePassword());
  };

  const handleRemovePassword = () => {
    setPasswordAction('remove');
    setPasswordValue('');
  };

  const handleCancelPasswordChange = () => setPasswordAction('unchanged');

  const handleSubmit = () => {
    updateGrant.mutate(
      { grantId: grant.id, input: buildUpdateInput(ttl, passwordAction, passwordValue) },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  const canSubmit =
    !updateGrant.isPending &&
    ((passwordAction !== 'add' && passwordAction !== 'regenerate') || passwordValue.trim().length > 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !updateGrant.isPending && onOpenChange(o)}>
      <DialogContent data-testid="edit-share-grant-dialog" className={SHARE_DIALOG_CLASS}>
        <DialogHeader>
          <DialogTitle>Edit share link</DialogTitle>
          <DialogDescription>Change how long this link stays active, or manage its password.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div role="group" aria-label="Link expiry" className="flex items-center rounded-full bg-[#F4F4F2] p-1">
            {TTL_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setTtl(opt.value)}
                aria-pressed={ttl === opt.value}
                className={cn(
                  'flex-1 rounded-full px-3 py-1.5 text-body-sm-medium leading-none transition-colors',
                  ttl === opt.value
                    ? 'bg-white text-[#171717] shadow-[0px_2px_6px_-1px_rgba(0,0,0,0.04),0px_1px_2px_rgba(0,0,0,0.04)]'
                    : 'text-[#737373] hover:text-[#171717]',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <p className="text-caption-regular text-muted-foreground">Expires {TTL_HUMAN[ttl]} from now</p>

          {!grant.password_protected ? (
            passwordAction === 'add' ? (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Input
                    aria-label="Share password"
                    value={passwordValue}
                    onChange={(e) => setPasswordValue(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPasswordValue(generateRandomSharePassword())}
                  >
                    Generate
                  </Button>
                </div>
                <Button type="button" variant="ghost" size="sm" className="self-start" onClick={handleCancelPasswordChange}>
                  Cancel password change
                </Button>
              </div>
            ) : (
              <Button type="button" variant="outline" onClick={handleAddPassword} className="self-start">
                <Lock size={14} />
                Add password
              </Button>
            )
          ) : (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                {/* Regenerate/Remove are radio-style: clicking either always
                    switches `passwordAction` directly (never disabled) — the
                    mutual exclusivity comes from both writing into the SAME
                    state variable (see buildUpdateInput), not from blocking
                    the click. aria-pressed reflects which one is selected. */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleRegeneratePassword}
                  aria-pressed={passwordAction === 'regenerate'}
                >
                  <RefreshCw size={14} />
                  Regenerate password
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleRemovePassword}
                  aria-pressed={passwordAction === 'remove'}
                >
                  Remove password
                </Button>
              </div>
              {passwordAction === 'regenerate' && (
                <div className="flex items-center gap-2">
                  <Input
                    aria-label="Share password"
                    value={passwordValue}
                    onChange={(e) => setPasswordValue(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPasswordValue(generateRandomSharePassword())}
                  >
                    Generate
                  </Button>
                </div>
              )}
              {passwordAction === 'remove' && (
                <p className="text-caption-regular text-muted-foreground">
                  This link will no longer require a password.
                </p>
              )}
              {passwordAction !== 'unchanged' && (
                <Button type="button" variant="ghost" size="sm" className="self-start" onClick={handleCancelPasswordChange}>
                  Cancel password change
                </Button>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={updateGrant.isPending}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
